import _ from 'lodash';
import Vue from 'vue';
import FormError from './FormErrors';
import FormField from './FormField';

export default class FormGroup extends FormError {
  constructor(fields) {
    super();
    this.errors = [];
    this.fields = {};

    if (!fields || !Object.keys(fields).length) {
      return new Error('One field is required to create a form group');
    }

    Object.keys(fields)
      .forEach((key) => {
        this.fields[key] = new FormField(fields[key]);
      });
  }

  getRawValue(camelCase) {
    const rawValue = {};
    Object.keys(this.fields)
      .forEach((fieldName) => {
        const field = this.fields[fieldName];

        if (field.ignoreOnGetRawValue) {
          return;
        }

        if (field.ignoreIfNot && !this.ignoreIfNotHaveMatch(field)) {
          field.value = null;
        }

        let { value } = field;
        if (field.cpf) {
          value = field.clearValue();
        }
        if (!camelCase) {
          rawValue[_.snakeCase(fieldName)] = value;
        } else {
          rawValue[fieldName] = value;
        }
      });

    return rawValue;
  }

  checkIfFieldMatchValue(valuesToMatch, value) {
    return (
      (Array.isArray(valuesToMatch) && valuesToMatch.includes(value))
      || (!(Array.isArray(valuesToMatch)) && value === valuesToMatch)
    );
  }

  ignoreIfNotHaveMatch(field) {
    let match = true;
    const keys = Object.keys(field.ignoreIfNot);
    for (let i = 0; i < keys.length; i += 1) {
      if (!this.checkIfFieldMatchValue(field.ignoreIfNot[keys[i]], this.getValue(keys[i]))) {
        match = false;
        break;
      }
    }
    return match;
  }

  validFieldsNumber() {
    let validFields = 0;
    let wrongFields = 0;

    Object.keys(this.fields)
      .forEach((fieldName) => {
        if (this.shouldIgnoreField(this.fields[fieldName])) {
          return;
        }
        if (this.isFieldValid(fieldName) && this.fields[fieldName].required) {
          validFields += 1;
        } else if (this.fields[fieldName].required) {
          wrongFields += 1;
        }
      });
    return { valids: validFields, wrongs: wrongFields, total: validFields + wrongFields };
  }

  validateForm() {
    this.errors = [];
    Object.keys(this.fields)
      .forEach((fieldName) => {
        this.validateField(fieldName);
      });
    return !this.errors.length;
  }

  validateField(fieldName) {
    const field = this.fields[fieldName];
    if (this.shouldIgnoreField(field)) {
      field.errors = [];
      field.error = '';
      field.valid = true;
      return;
    }
    if (field.requiredIfMatch) {
      this.requiredIfMatch(field);
    }

    if (!field.validateField()) {
      this.errors.push(fieldName);
    }

    if (field.matchPassword && field.value !== this.fields[field.matchPassword].value) {
      this.errors.push(fieldName);
      field.errors.push(this.matchPasswordError());
    }

    field.error = field.errors[0] || '';
    field.valid = field.errors.length === 0;
  }

  shouldIgnoreField(field) {
    return field.ignoreIfNot && !this.ignoreIfNotHaveMatch(field);
  }

  isFieldValid(fieldName) {
    const field = this.fields[fieldName];

    if (field.requiredIfMatch) {
      this.requiredIfMatch(field);
    }

    return field.validateField(true) && (
      !field.matchPassword
      || (field.matchPassword && field.value === this.fields[field.matchPassword].value)
    );
  }

  addValues(fields) {
    Object.keys(fields)
      .forEach((fieldName) => {
        if (this.fields[fieldName]) {
          Vue.set(this.fields[fieldName], 'value', fields[fieldName]);
          this.fields[fieldName].validateField(true);
          if (this.fields[fieldName].onAddValueReplicateTo) {
            this.replicateValueTo(
              _.get(this.fields[fieldName], this.fields[fieldName].onAddValueReplicateTo.path),
              this.fields[fieldName].onAddValueReplicateTo.to,
            );
          }
        }
      });
  }

  replicateValueTo(value, to) {
    const field = _.get(this.fields, to);
    Vue.set(field, 'value', value);
  }

  addErrors(fields) {
    Object.keys(fields)
      .forEach((fieldName) => {
        this.fields[fieldName].errors.push(fields[fieldName]);
        this.errors.push(fieldName);
      });
  }

  get(key) {
    return this.fields[key];
  }

  getValue(key) {
    return this.fields[key].value;
  }

  clearValues(fields) {
    fields
      .forEach((fieldName) => {
        if (this.fields[fieldName]) {
          this.fields[fieldName].value = null;
        }
      });
  }

  clearValuesExcept(fields) {
    Object.keys(this.fields)
      .forEach((fieldName) => {
        if (!fields.includes(fieldName)) {
          this.fields[fieldName].value = null;
        }
      });
  }

  requiredIfMatch(field) {
    const rules = field.requiredIfMatch;
    Object.keys(rules)
      .forEach((fieldName) => {
        const rule = rules[fieldName];
        switch (rule.condition) {
          case '!':
            field.required = true;
            rule.values
              .forEach((value) => {
                if (value === this.fields[fieldName].value) {
                  field.required = false;
                }
              });
            break;
          default:
            field.required = false;
            rule.values
              .forEach((value) => {
                if (value === this.fields[fieldName].value) {
                  field.required = true;
                }
              });
        }
      });
    return field.required;
  }
}
